import {
  LocalStorage as AppLocalStorage,
  SessionStorage as AppSessionStorage,
} from './app-storage';
import { StandardStorageKeys } from './standard-keys';

/**
 * Helper function to check if a value is null or undefined
 */
function isNullOrUndefined(value: any): value is null | undefined {
  return value === null || value === undefined;
}

/**
 * LocalStorage module definition.
 * Provides storage helper with key management and legacy key support.
 * Apps can provide their own custom keys, while standard keys are managed by the library.
 */
export type LocalStorageType = 'temporary' | 'permanent';

/**
 * Type for storage keys definition
 */
export type StorageKeys = Record<string, string[]>;

/**
 * Creates a storage helper instance with custom app-specific keys
 * Standard keys (TC_ACCESS_TOKEN, TRIMBLE_SERVICES, TRIMBLE_SERVICES_STAGE) are automatically included
 *
 * @param customKeys Optional app-specific keys to add to the standard keys
 * @param type Storage type (permanent for LocalStorage, temporary for SessionStorage)
 * @returns Storage helper instance
 */
export function createStorageHelper(
  customKeys?: StorageKeys,
  type: LocalStorageType = 'permanent'
) {
  // Merge standard keys with custom keys (custom keys take precedence if there's a conflict)
  const Keys: StorageKeys = {
    ...StandardStorageKeys,
    ...customKeys,
  };

  const AppStorage = type === 'permanent' ? AppLocalStorage : AppSessionStorage;

  function getItem(keys: string[]): string | null {
    for (const key of keys) {
      const value = AppStorage.getItem(key);
      if (value) {
        return value;
      }
    }
    return null;
  }

  function setItem(key: string[], value: string): void {
    AppStorage.setItem(key[0], value);
  }

  function removeItem(keys: string[]): void {
    for (const key of keys) {
      const value = AppStorage.getItem(key);
      if (!isNullOrUndefined(value)) {
        AppStorage.removeItem(key);
      }
    }
  }

  function getItemAsArray<T>(keys: string[]): T[] {
    const item = getItem(keys);
    return (item ? JSON.parse(item) : []) as T[];
  }

  function setItemAsArray<T>(keys: string[], value: T[]): void {
    setItem(keys, JSON.stringify(value));
  }

  function clear(): void {
    return AppStorage.clear();
  }

  return {
    Keys,
    getItem,
    setItem,
    removeItem,
    getItemAsArray,
    setItemAsArray,
    clear,
  };
}

/**
 * Default storage helper instances (for backward compatibility)
 * These use only the standard keys. Apps should create their own instances with custom keys.
 */
export const LocalStorage = createStorageHelper(undefined, 'permanent');
export const SessionStorage = createStorageHelper(undefined, 'temporary');
